import type { Locale } from '../paraglide/runtime.js';
import type { DefinedPath } from './definePath';

type TranslatedPathname = {
  pattern: string;
  localized: Array<[Locale, string]>;
};

export type TranslatedPathnamesInput<TPath extends string = string> = Record<TPath, Record<Locale, string>>;
type ResolvedTranslatedPathnamesInput = Record<string, Record<Locale, string>>;

/** Shape of a `route.paths.ts` module: each named export is a record of `DefinedPath`s. */
export type RoutePathsModule = Record<string, Record<string, DefinedPath>>;
/** A discovered route.paths module paired with its file path (used in error messages). */
export type RoutePathsEntry = readonly [path: string, mod: RoutePathsModule];

function toUrlPattern(path: string) {
  return (
    path
      // catch-all
      .replace(/\/\$$/, '/:path(.*)?')
      // optional parameters: {-$param}
      .replace(/\{-\$([a-zA-Z0-9_]+)\}/g, ':$1?')
      // named parameters: $param
      .replace(/\$([a-zA-Z0-9_]+)/g, ':$1')
      // remove trailing slash
      .replace(/\/+$/, '')
  );
}

export function createTranslatedPathnames(input: ResolvedTranslatedPathnamesInput, baseLocale: Locale): TranslatedPathname[] {
  return Object.entries(input).map(([pattern, locales]) => ({
    pattern: toUrlPattern(pattern),
    localized: Object.entries(locales).map(
      ([locale, path]) =>
        [locale as Locale, locale === baseLocale ? toUrlPattern(path) : `/${locale}${toUrlPattern(path)}`] satisfies [Locale, string],
    ),
  }));
}

/**
 * Merges the `<Domain>Paths` records exported from a set of route.paths modules
 * into a flat path → locales map. Single source of truth for the discovery
 * convention — both the runtime glob and the config-time fs walk in
 * `scripts/paraglide-options.ts` feed through here so the merge contract can't
 * drift between contexts. Iterates every named
 * export, so a domain may split its paths across multiple records if it wants.
 * Throws when a module exports no records so a forgotten `export const …Paths`
 * surfaces at boot, not as silently missing URLs.
 */
export function mergeTranslatedPathnames(entries: Iterable<RoutePathsEntry>): TranslatedPathnamesInput<string> {
  const merged: TranslatedPathnamesInput<string> = {};
  for (const [path, mod] of entries) {
    const records = Object.values(mod);
    if (records.length === 0) {
      throw new Error(`route.paths module ${path} has no named exports of DefinedPath records`);
    }
    for (const paths of records) {
      for (const def of Object.values(paths)) {
        merged[def.path] = def.translations;
      }
    }
  }
  return merged;
}
